import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  FilterList,
  Clear,
  Hotel,
  Event,
  Campaign,
} from '@mui/icons-material';
import { PartnershipFilters as Filters, PartnershipType } from '@/services/partnershipsService';

interface PartnershipFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  onClearFilters: () => void;
}

const PartnershipFiltersComponent: React.FC<PartnershipFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: event.target.value, page: 1 });
  };

  const handleTypeChange = (event: any) => {
    onFilterChange({ ...filters, type: event.target.value, page: 1 });
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    onFilterChange({ ...filters, isActive: value === 'all' ? 'all' : value === 'true', page: 1 });
  };

  const handleSortByChange = (event: any) => {
    onFilterChange({ ...filters, sortBy: event.target.value });
  };

  const handleSortOrderChange = (event: any) => {
    onFilterChange({ ...filters, sortOrder: event.target.value });
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, dateFrom: event.target.value, page: 1 });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, dateTo: event.target.value, page: 1 });
  };

  // Calculate active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.type && filters.type !== 'all') count++;
    if (filters.isActive !== undefined && filters.isActive !== 'all') count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Quick filter buttons
  const quickFilters = [
    { label: 'All', value: 'all' as const },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
      {/* Search Bar and Quick Filters */}
      <Box mb={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company name, email, or referral code..."
              value={filters.search || ''}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => onFilterChange({ ...filters, search: '', page: 1 })}
                    >
                      <Clear fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={1} flexWrap="wrap">
              {quickFilters.map((qf) => (
                <Chip
                  key={String(qf.value)}
                  label={qf.label}
                  onClick={() => onFilterChange({ ...filters, isActive: qf.value, page: 1 })}
                  color={filters.isActive === qf.value ? 'primary' : 'default'}
                  variant={filters.isActive === qf.value ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Advanced Filters Accordion */}
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList />
            <Typography>Advanced Filters</Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={`${activeFilterCount} active`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Partnership Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Partnership Type</InputLabel>
                <Select
                  value={filters.type || 'all'}
                  onChange={handleTypeChange}
                  label="Partnership Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="hotel">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Hotel fontSize="small" />
                      <span>Hotel</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="event_planner">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Event fontSize="small" />
                      <span>Event Planner</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="promoter">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Campaign fontSize="small" />
                      <span>Promoter</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === 'all' ? 'all' :
                    filters.isActive === true ? 'true' :
                    filters.isActive === false ? 'false' : 'all'
                  }
                  onChange={handleStatusChange}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy || 'activatedAt'}
                  onChange={handleSortByChange}
                  label="Sort By"
                >
                  <MenuItem value="companyName">Company Name</MenuItem>
                  <MenuItem value="activatedAt">Activation Date</MenuItem>
                  <MenuItem value="totalRevenue">Total Revenue</MenuItem>
                  <MenuItem value="totalBookings">Total Bookings</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort Order */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={filters.sortOrder || 'desc'}
                  onChange={handleSortOrderChange}
                  label="Sort Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date From */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Activated From"
                value={filters.dateFrom || ''}
                onChange={handleDateFromChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Date To */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Activated To"
                value={filters.dateTo || ''}
                onChange={handleDateToChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Clear />}
                onClick={onClearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default PartnershipFiltersComponent;
